import { Modal, ModalHeader, ModalBody } from "@design-lab/shared/ui/modal";
import type { Color, Product } from "@design-lab/shared/types/base";
import { useCompositionsActions } from "@design-lab/entities/composition";
import { ColorPalette } from "@design-lab/shared/ui/ColorPalette";
import { SelectedColorDisplay } from "@design-lab/shared/ui/SelectedColorDisplay";

type AddColorModalProps = {
  isOpen: boolean;
  onClose: () => void;
  recommendedColors: Color[];
  otherColors: Color[];
  currentProduct: Product | undefined;
  selectedColor: Color | undefined;
  onColorSelect: (id: string) => void;
  maxColors: number;
};

export function AddColorModal({
  isOpen,
  onClose,
  recommendedColors,
  otherColors,
  currentProduct,
  selectedColor,
  onColorSelect,
  maxColors
}: AddColorModalProps) {
  const { previewComposition, clearCompositionPreview } = useCompositionsActions();

  const handleColorSelect = (id: string) => {
    onColorSelect(id);
    onClose();
    clearCompositionPreview(); // Clear override when selecting
  };

  const handleColorHover = (colorId: string) => {
    const color = [...recommendedColors, ...otherColors].find(c => c.id === colorId);
    if (!color) return;

    const newComposition = currentProduct?.compositions.find(comp => comp.colorName === color.name);
    if (newComposition) {
      previewComposition(newComposition.id);
    }
  };

  const handleColorBlur = () => {
    clearCompositionPreview();
  };

  // Clear override when modal closes
  const handleClose = () => {
    clearCompositionPreview();
    onClose();
  };

  if (!currentProduct) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Color"
      className="mx-auto h-auto max-h-[90vh] w-full max-w-[96%] xl:max-w-2xl"
    >
      <ModalHeader onClose={handleClose} className="justify-start sm:justify-center">
        Add this product in another color
      </ModalHeader>
      <ModalBody className="space-y-6">
        {recommendedColors.length === 0 && otherColors.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-gray-600">No colors available for this product.</p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <h3 className="mb-2 text-lg font-semibold">Select a color to add:</h3>
              {selectedColor && (
                <SelectedColorDisplay selectedColor={selectedColor} className="lg:hidden" />
              )}
              <div className="max-h-96 overflow-y-auto">
                <ColorPalette
                  recommendedColors={recommendedColors}
                  otherColors={otherColors}
                  onColorSelect={handleColorSelect}
                  onColorHover={handleColorHover}
                  onColorBlur={handleColorBlur}
                  selectedColor={selectedColor || recommendedColors[0] || otherColors[0]!}
                  showToggleButton={false}
                  showRecommendedToggle={false}
                  showAllColors={true}
                  showAllRecommended={true}
                  maxColors={maxColors}
                />
              </div>
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  );
}
